/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { useNotificationContext, useSendMoneyContext, useUserDetailsContext } from "@zenith/context";
import { formatCurrency } from "@zenith/utils";
import {
  ApiResponseDTOOperationResFeeRecord,
  OperationsApiService,
  OperationReqBaseDTO,
} from "@zenith/services/transactions";
import { SEND_MONEY, SEND_MONEY_PIN } from "@zenith/routes/paths";
import {
  CURRENT_ZENITH_NIGERIA_BANK_ID,
  FINOPAY_NIGERIA_BANK_CODE,
  WALLET,
  ZENITH_NIGERIA_BANK_ID,
} from "@zenith/data/constants";
import { ButtonComponent, SpinnerComponent, ToastSnackbarComponent } from "@zenith/components";
import { TRANSFER_OPTIONS } from "@zenith/pages/SendMoneyPage/constants";
import ArrowLeft from "@zenith/assets/icons/ArrowLeft";

const BENEFICIARY_TOAST_SEVERITY: Record<
  string,
  "warning" | "success" | "error"
> = {
  duplicate: "warning",
  success: "success",
  failed: "error",
};

const SendMoneyOverviewPage = memo(function SendMoneyOverviewPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const { sendMoneyContext, setSendMoneyContext } = useSendMoneyContext();
  const { userDetailsContext } = useUserDetailsContext();

  const availableBalance: number = location.state?.availableBalance ??
    userDetailsContext?.availableBalance ?? 0;

  const {
    amount,
    bankId,
    bankName,
    beneficiaryAccountNumber,
    beneficiaryName,
    feeValue,
    totalAmount,
    saveBeneficiaryStatus,
    transferType,
    vatValue,
  } = sendMoneyContext;

  const { setOpen } = useNotificationContext();
  const [isLoadingFee, setIsLoadingFee] = useState(true);
  const [feeFetchError, setFeeFetchError] = useState(false);
  const [beneficiaryToastStatus] = useState(saveBeneficiaryStatus);

  useEffect(() => {
    setIsLoadingFee(true);
    setFeeFetchError(false);

    if (!amount || !beneficiaryAccountNumber || !beneficiaryName) {
      setIsLoadingFee(false);
      return;
    }

    OperationsApiService.postOperationFee({
      currency: OperationReqBaseDTO.currency.NGN,
      amount,
      beneficiary: {
        accountNumber: beneficiaryAccountNumber,
        bank: {
          bankName:
            transferType === WALLET.TRANSFER_TYPE ? WALLET.BANK_NAME : bankName,
          id:
            transferType === WALLET.TRANSFER_TYPE
              ? FINOPAY_NIGERIA_BANK_CODE
              : bankId,
        },
        name: beneficiaryName,
      },
    })
      .then((response: ApiResponseDTOOperationResFeeRecord) => {
        if (response.success && amount === response.data?.amount) {
          const feeBeneficiary = response.data.beneficiary;

          setSendMoneyContext({
            ...sendMoneyContext,
            amount: response.data.amount,
            feeValue: response.data.feeValue,
            totalAmount: response.data.totalAmount,
            vatValue: response.data.vat,
            saveBeneficiaryStatus: undefined,
            ...(feeBeneficiary && {
              beneficiaryName: feeBeneficiary.name,
              beneficiaryAccountNumber: feeBeneficiary.accountNumber,
              bankName: feeBeneficiary.bank?.bankName ?? bankName,
              bankId: feeBeneficiary.bank?.id ?? bankId,
            }),
            ...(transferType === TRANSFER_OPTIONS.WALLET && {
              bankName: WALLET.BANK_NAME,
              bankId: FINOPAY_NIGERIA_BANK_CODE,
            }),
          });
          setFeeFetchError(false);
        } else {
          setFeeFetchError(true);
        }
      })
      .catch((error) => {
        console.error(error.message);
        setFeeFetchError(true);
      })
      .finally(() => setIsLoadingFee(false));

    if (saveBeneficiaryStatus) {
      setOpen(true);
      setSendMoneyContext({
        ...sendMoneyContext,
        saveBeneficiaryStatus: undefined,
      });
    }
  }, []);

  if (!amount || !beneficiaryAccountNumber || !beneficiaryName) {
    return <Navigate to={SEND_MONEY} replace />;
  }

  function handleSubmit(): void {
    if (transferType === TRANSFER_OPTIONS.WALLET) {
      navigate(SEND_MONEY_PIN, {
        state: {
          nameEnquiry: beneficiaryAccountNumber,
          feeValue: feeValue,
          availableBalance: availableBalance,
        },
      });
      return;
    }

    navigate(SEND_MONEY_PIN, {
      state: {
        nameEnquiry: location.state?.nameEnquiry,
        feeValue: feeValue,
        availableBalance: availableBalance,
      },
    });
  }

  function handleBackButton(): void {
    navigate(SEND_MONEY, {
      state: {
        fromOverview: true,
        availableBalance: availableBalance
      }
    });
  }

  function getNotificationDetails() {
    switch (beneficiaryToastStatus) {
      case "duplicate":
        return {
          title: "Beneficiary Already Exists",
          text: "The beneficiary is already in your list.",
        };
      case "success":
        return {
          title: "Beneficiary Saved Successfully",
          text: "The new beneficiary has been added successfully.",
        };
      case "failed":
      default:
        return {
          title: "Unable to Save Beneficiary",
          text: "We couldn't save the new beneficiary. Please try again.",
        };
    }
  }

  const toastSeverity = beneficiaryToastStatus
    ? BENEFICIARY_TOAST_SEVERITY[beneficiaryToastStatus] ?? "error"
    : "error";

  const isConfirmDisabled =
    isLoadingFee ||
    feeFetchError ||
    (transferType !== TRANSFER_OPTIONS.WALLET &&
      feeValue == null &&
      bankId !== CURRENT_ZENITH_NIGERIA_BANK_ID) ||
    availableBalance < (totalAmount ?? 0);

  return (
    <>
      {beneficiaryToastStatus && (
        <ToastSnackbarComponent
          horizontal="center"
          status={toastSeverity}
          vertical="top"
          className="mt-16 ml-32"
          hasAutoHideDuration
          size="lg"
          title={getNotificationDetails().title}
          text={getNotificationDetails().text}
        />
      )}
      <div className="mb-3 flex-col items-start justify-between ">
        <ButtonComponent
          label="Back"
          onClick={() => handleBackButton()}
          id="back-to-dashboard"
          size="large"
          type="tertiary"
          iconStart={<ArrowLeft width={24} height={24} />}
          className="!gap-1.5 !text-[16px] !font-normal !leading-5 !text-neutral-600 !no-underline"
        />
        <div className="m-auto mx-2 mt-5 w-full rounded-[20px] border border-neutral-200 lg:w-[668px]">
          <div className="rounded-2.5xl bg-white pt-6 px-8 pb-8">
            <div className="flex-col justify-between">
              <h1 className="mb-8 text-2xl font-medium text-neutral-900">
                Transfer Overview
              </h1>
            </div>

            <div className="flex flex-col gap-4 whitespace-nowrap text-base leading-5 text-neutral-900">
              <div className="flex flex-row items-center justify-between">
                <p>Beneficiary Account</p>
                <p className="font-medium leading-6">{beneficiaryAccountNumber}</p>
              </div>
              <div className="flex flex-row items-center justify-between">
                <p>Bank Name</p>
                <p className="font-medium leading-6">{bankName}</p>
              </div>
              <div className="flex flex-row items-center justify-between">
                <p>Beneficiary Name</p>
                <p className="font-medium leading-6">{beneficiaryName}</p>
              </div>
            </div>
            <hr className="my-6 border-t-1 border-neutral-200" />
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-4 text-base font-medium leading-6 text-neutral-900">
                <div className="flex flex-row items-center justify-between">
                  <p>Amount</p>
                  <p>{formatCurrency(amount)}</p>
                </div>
                <div className="flex flex-row items-center justify-between">
                  <p>
                    Fee (VAT Inclusive)
                    {isLoadingFee && (
                      <span className="ml-2">
                        <SpinnerComponent size={16} type="primary" />
                      </span>
                    )}
                    {feeFetchError && (
                      <span className="ml-2 text-sm text-red-500">Failed to load</span>
                    )}
                  </p>
                  <p>
                    {isLoadingFee ? (
                      <span className="text-neutral-400">--</span>
                    ) : feeFetchError ? (
                      <span className="text-red-500">--</span>
                    ) : (
                      formatCurrency((feeValue ?? 0) + (vatValue ?? 0))
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-row items-center justify-between text-base leading-6 text-neutral-900">
                <p className="font-semibold">Total Amount</p>
                <p className="font-medium">
                  {isLoadingFee ? (
                    <span className="text-neutral-400">--</span>
                  ) : feeFetchError ? (
                    <span className="text-red-500">--</span>
                  ) : (
                    formatCurrency(totalAmount)
                  )}
                </p>
              </div>
            </div>

            {!isLoadingFee && !feeFetchError && availableBalance < (totalAmount ?? 0) && (
              <p className="mt-6 text-center text-sm text-red-500">
                Insufficient balance. Your available balance is{" "}
                {formatCurrency(availableBalance)}.
              </p>
            )}

            {feeFetchError && (
              <p className="mt-6 text-center text-sm text-red-500">
                Failed to calculate fees. Please try again.
              </p>
            )}

            <div className="mt-[104px] flex justify-center">
              <ButtonComponent
                label="Confirm"
                onClick={handleSubmit}
                disabled={isConfirmDisabled}
                size="large"
                type="primary"
                id="confirm-send-money-button"
                isLoading={isLoadingFee}
                className="!w-[212px] !rounded-lg !px-8 !py-3 !text-[16px] !font-normal !leading-5"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export default SendMoneyOverviewPage;
